/*
 * KC Theme — priority+ header navigation.
 *
 * Keeps the top-level menu on a single line: links that no longer fit the nav
 * column are hidden from the bar and revealed inside the "More" dropdown, which
 * is rendered server-side. If this script never runs the menu behaves exactly as
 * before (all links in the bar, no "More"), so nothing becomes unreachable.
 *
 * Widths are measured once and cached, so a resize is pure arithmetic — no
 * per-frame layout thrashing.
 */
customElements.define(
  'header-nav',
  class HeaderNav extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-nav-list]');
      this.more = this.querySelector('[data-nav-more]');
      this.moreLink = this.querySelector('[data-nav-more-link]');
      if (!this.list || !this.more) return;

      this.items = Array.from(this.list.children).filter((el) => el.dataset.navIndex !== undefined);
      this.spill = Array.from(this.querySelectorAll('[data-nav-spill] > [data-nav-index]'));
      if (this.items.length < 2) return;

      this.gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      this.visible = -1;

      this.ro = new ResizeObserver(() => this.layout());
      this.ro.observe(this);
      this.layout();

      // Web fonts swapping in changes every pill width — re-measure once.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this.widths = null;
          this.layout();
        });
      }
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
    }

    /* Everything must be on-screen for offsetWidth to be meaningful. */
    measure() {
      this.items.forEach((li) => {
        li.style.display = '';
      });
      this.more.style.display = '';
      this.widths = this.items.map((li) => li.offsetWidth);
      this.moreWidth = this.more.offsetWidth;
      this.visible = -1;
    }

    layout() {
      const avail = this.clientWidth;
      if (!avail) return;
      if (!this.widths) this.measure();

      const total = this.widths.reduce((sum, w) => sum + w, 0) + this.gap * (this.widths.length - 1);
      let visible = this.items.length;

      if (total > avail) {
        const budget = avail - this.moreWidth - this.gap;
        let used = 0;
        visible = 0;
        for (let i = 0; i < this.widths.length; i += 1) {
          const w = this.widths[i] + (visible ? this.gap : 0);
          if (used + w > budget) break;
          used += w;
          visible += 1;
        }
      }

      if (visible === this.visible) return;
      this.visible = visible;

      this.items.forEach((li, i) => {
        li.style.display = i < visible ? '' : 'none';
      });
      this.spill.forEach((li) => {
        li.style.display = Number(li.dataset.navIndex) < visible ? 'none' : '';
      });
      this.more.style.display = visible < this.items.length ? '' : 'none';

      // "More" wears the active pill when the current page's link is inside it.
      if (this.moreLink) {
        const holdsActive = this.spill.some(
          (li) => Number(li.dataset.navIndex) >= visible && li.dataset.navActive === 'true'
        );
        this.moreLink.classList.toggle('nav-pill--active', holdsActive);
      }
    }
  }
);
